(() => {
  const cache = new Map();
  const normalize = value => value.trim().replace(/\s+/g, ' ').slice(0, 120)
    .replace(/\byatch\b/giu, 'yacht')
    .replace(/\bboard[ -]+walk\b/giu, 'boardwalk')
    .replace(/\bpoly\b/giu, 'polynesian');
  const words = new Map();
  let vocabulary;
  const fold = value => value.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();
  const distance = (a, b) => {
    const rows = Array.from({ length: a.length + 1 }, (_, i) => [i]);
    for (let j = 1; j <= b.length; j++) rows[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        rows[i][j] = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1);
      }
    }
    return rows[a.length][b.length];
  };
  const spelling = value => value.replace(/[\p{L}\p{N}]+/gu, token => {
    const word = fold(token);
    if (word.length < 4 || /\d/u.test(word) || words.has(word)) return token;
    for (const candidate of words.keys()) if (candidate.startsWith(word)) return token;
    const limit = word.length >= 7 ? 2 : 1;
    let best = word;
    let score = Infinity;
    let second = Infinity;
    for (const [candidate, count] of words) {
      if (Math.abs(candidate.length - word.length) > limit) continue;
      const edits = distance(word, candidate);
      if (edits > limit) continue;
      const rank = edits - Math.min(.45, Math.log2(count + 1) * .09);
      if (rank < score) { second = score; score = rank; best = candidate; }
      else if (rank < second) second = rank;
    }
    return second - score >= .08 ? best : token;
  });
  const loadVocabulary = root => {
    if (vocabulary) return vocabulary;
    vocabulary = (async () => {
      let page = 1;
      for (let i = 0; page && i < 8; i++) {
        const url = new URL(root, window.location.origin);
        url.search = new URLSearchParams({ section_id: 'pw-search-vocabulary', page: String(page) }).toString();
        const response = await fetch(url, { signal: AbortSignal.timeout(3500), headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search vocabulary unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = parsed.querySelector('[data-search-vocabulary]');
        if (!content) throw new Error('Search vocabulary unavailable');
        const data = JSON.parse(content.textContent);
        for (const word of fold(data.text).match(/[\p{L}]{3,24}/gu) || []) words.set(word, (words.get(word) || 0) + 1);
        page = data.next;
      }
    })().catch(() => { vocabulary = undefined; });
    return vocabulary;
  };
  class PinwallSearch extends HTMLElement {
    connectedCallback() {
      if (this.life && !this.life.signal.aborted) return;
      this.input = this.querySelector('input[name="q"]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('[data-search-panel]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.progress = this.querySelector('[data-search-progress]');
      if (!this.input || !this.results || !this.form || !this.panel || !this.status) return;
      this.life = new AbortController();
      this.version = 0;
      this.active = -1;
      const options = { signal: this.life.signal };
      this.input.addEventListener('input', event => { if (!event.isComposing) this.schedule(); }, options);
      this.input.addEventListener('compositionend', () => this.schedule(), options);
      this.input.addEventListener('focus', () => this.schedule(), options);
      this.input.addEventListener('keydown', event => this.onKey(event), options);
      this.form.addEventListener('formdata', event => event.formData.set('q', spelling(normalize(this.input.value))), options);
      this.form.addEventListener('submit', async event => {
        if (this.readyToSubmit || normalize(this.input.value).length < 3) return;
        event.preventDefault();
        if (this.waitingToSubmit) return;
        this.waitingToSubmit = true;
        await Promise.race([loadVocabulary(this.dataset.searchRoot), new Promise(resolve => setTimeout(resolve, 2500))]);
        if (!this.isConnected) return;
        this.readyToSubmit = true;
        this.waitingToSubmit = false;
        this.form.requestSubmit(event.submitter);
      }, options);
      this.form.addEventListener('change', event => { if (event.target.name === 'filter.v.availability') this.schedule(); }, options);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.close(); }, options);
      document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.close(); }, options);
      this.results.addEventListener('pointermove', () => this.select(-1), options);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.life?.abort();
    }
    close() {
      clearTimeout(this.timer);
      this.version++;
      this.request?.abort();
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.results.removeAttribute('aria-busy');
      this.select(-1);
    }
    select(index) {
      this.options = Array.from(this.results.querySelectorAll('[role="option"]'));
      this.options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
      this.active = index;
      if (index < 0 || !this.options[index]) this.input.removeAttribute('aria-activedescendant');
      else {
        this.input.setAttribute('aria-activedescendant', this.options[index].id);
        this.options[index].scrollIntoView({ block: 'nearest' });
      }
    }
    async onKey(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape' && !this.panel.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      } else if (event.key === 'Escape' && this.closest('[data-search-dialog]')) {
        event.preventDefault();
        event.stopPropagation();
        this.closest('[data-search-dialog]').close();
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (normalize(this.input.value).length < 2) return;
        event.preventDefault();
        if (this.panel.hidden) await this.lookup();
        const count = this.results.querySelectorAll('[role="option"]').length;
        if (!count) return;
        this.select(event.key === 'ArrowDown' ? (this.active + 1) % count : (this.active < 0 ? count - 1 : (this.active - 1 + count) % count));
      } else if (event.key === 'Enter' && !this.panel.hidden && this.active >= 0 && this.options[this.active]) {
        event.preventDefault();
        this.options[this.active].click();
      }
    }
    schedule() {
      clearTimeout(this.timer);
      this.version++;
      this.request?.abort();
      this.select(-1);
      this.results.replaceChildren();
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      if (normalize(this.input.value).length < 2) {
        this.status.textContent = '';
        return;
      }
      this.timer = setTimeout(() => this.lookup(), 180);
      if (normalize(this.input.value).length >= 3 && !this.vocabularyRequested) {
        this.vocabularyRequested = true;
        loadVocabulary(this.dataset.searchRoot).then(() => {
          if (this.isConnected && document.activeElement === this.input && normalize(this.input.value).length >= 2) this.lookup();
        });
      }
    }
    async lookup() {
      clearTimeout(this.timer);
      const waitingVersion = this.version;
      if (vocabulary && !words.size) await Promise.race([vocabulary, new Promise(resolve => setTimeout(resolve, 1200))]);
      if (waitingVersion !== this.version || !this.isConnected) return;
      const query = spelling(normalize(this.input.value));
      if (query.length < 2) return;
      this.request?.abort();
      const version = ++this.version;
      this.request = new AbortController();
      const url = new URL(this.dataset.searchEndpoint, window.location.origin);
      const availableOnly = this.form.querySelector('[name="filter.v.availability"]')?.checked;
      url.search = new URLSearchParams({ q: query, section_id: 'pw-predictive-search', 'resources[type]': 'query,product,collection', 'resources[limit]': '8', 'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': availableOnly ? 'hide' : 'last' }).toString();
      this.panel.hidden = false;
      this.progress.hidden = false;
      this.progress.textContent = this.status.dataset.loading;
      this.results.setAttribute('aria-busy', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      this.status.textContent = this.status.dataset.loading;
      try {
        const key = url.toString();
        const cached = cache.get(key);
        let html;
        if (cached && Date.now() - cached.time < 20000) html = cached.html;
        else {
          const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Search unavailable');
          html = await response.text();
          cache.set(key, { html, time: Date.now() });
          if (cache.size > 20) cache.delete(cache.keys().next().value);
        }
        if (version !== this.version || !this.isConnected) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector('[data-search-response]');
        if (!content) throw new Error('Search response unavailable');
        this.results.replaceChildren(content);
        this.results.querySelectorAll('[role="option"]').forEach((option, i) => { option.id = `${this.input.id}-Option-${i}`; });
        this.select(-1);
        this.status.textContent = this.status.dataset.count.replace('__COUNT__', content.dataset.count);
        this.progress.hidden = true;
      } catch (error) {
        if (error.name === 'AbortError' || version !== this.version) return;
        this.results.replaceChildren();
        this.status.textContent = this.status.dataset.error;
        this.progress.textContent = this.status.dataset.error;
        this.progress.hidden = false;
      } finally {
        if (version === this.version) this.results.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('pinwall-search')) customElements.define('pinwall-search', PinwallSearch);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-search-open]');
    const dialog = document.querySelector('[data-search-dialog]');
    if (trigger && dialog?.showModal && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      document.querySelectorAll('.mobile-menu[open],.atelier-shop-menu[open]').forEach(menu => { menu.open = false; });
      if (!dialog.open) dialog.showModal();
      document.documentElement.classList.add('pw-search-is-open');
      dialog.querySelector('input[name="q"]')?.focus();
    }
    if (event.target.closest('[data-search-close]')) dialog?.close();
    if (event.target === dialog) {
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    }
  });
  const dialog = document.querySelector('[data-search-dialog]');
  dialog?.addEventListener('close', () => {
    document.documentElement.classList.remove('pw-search-is-open');
    dialog.querySelector('pinwall-search')?.close();
  });
})();
